import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, Button, Link, TextField } from '@material-ui/core';
import { MCP_CATALOG_MODE } from '../McpCatalogConstant';

const isSafeUrl = (url) => {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
        return false;
    }
};

function McpCatalogUrlField({ name, label, required, mode, defaultValue: initialValue }) {
    const { errors, control, trigger, setValue, clearErrors } = useFormContext();

    // Both Create and Edit mode start in view state (disabled input + edit icon)
    const [isEditing, setIsEditing] = useState(false);

    let isError = false;
    let errorMessage = '';
    if (errors && errors.hasOwnProperty(name)) {
        isError = true;
        errorMessage = errors[name].message;
    }

    /**
     * Reset button handler — mirrors RenderFieldUI.js handleOnReset behavior:
     * 1. Clear errors on this field
     * 2. Reset value to the initial/default value
     * 3. Switch back to view state (if edit mode)
     */
    const handleReset = useCallback((onChange) => {
        const resetValue = initialValue || '';
        setValue(name, resetValue);
        onChange(resetValue);
        setIsEditing(false);
        // clearErrors must be called after setValue settles to prevent
        // validation from re-triggering the error immediately
        setTimeout(() => {
            clearErrors(name);
        }, 0);
    }, [name, initialValue, clearErrors, setValue]);

    /**
     * Apply button handler:
     * Trigger validation; if no error, switch to view state
     */
    const handleApply = useCallback(async (value) => {
        const isValid = await trigger(name);
        if (isValid && value) {
            setIsEditing(false);
        }
    }, [name, trigger]);

    /**
     * onBlur handler: trigger validation to show required error if empty
     */
    const handleBlur = useCallback(() => {
        trigger(name);
    }, [name, trigger]);

    return (
        <div className="form-addTicket form-field-url">
            <label className="form-addTicket-label">
                {label}
                {required && <span className="required">*</span>}
            </label>
            <div className="form-addTicket-input">
                <Controller
                    name={name}
                    control={control}
                    render={({ value, onChange }) => (
                        <>
                            {/* View state: shows URL as hyperlink + edit icon (when value exists) */}
                            <Box style={{ display: !isEditing && value ? 'block' : 'none' }}>
                                <Box display="inline-flex" sx={{ width: '100%' }}>
                                    <Box className="field-url-link">
                                        <Link
                                            href={isSafeUrl(value) ? value : '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {value}
                                        </Link>
                                    </Box>
                                    <Button
                                        className="button-url cursor-default"
                                        style={{ paddingRight: '0px' }}
                                    >
                                        <img
                                            src="/assets/image/button-url-edit.svg"
                                            alt=""
                                            className="cursor-pointer"
                                            onClick={() => setIsEditing(true)}
                                        />
                                    </Button>
                                </Box>
                            </Box>

                            {/* View state: shows disabled input + edit icon (when no value) */}
                            <Box style={{ display: !isEditing && !value ? 'block' : 'none' }}>
                                <Box display="inline-flex" sx={{ width: '100%' }}>
                                    <Box sx={{ flexGrow: 1, paddingRight: '10px' }}>
                                        <TextField
                                            value=""
                                            variant="outlined"
                                            className="form-control-input form-control-wrapInput form-control-hg40"
                                            autoComplete="off"
                                            error={isError}
                                            helperText={errorMessage}
                                            fullWidth
                                            disabled
                                        />
                                    </Box>
                                    <Button
                                        className="button-url cursor-default"
                                        style={{ paddingRight: '0px' }}
                                    >
                                        <img
                                            src="/assets/image/button-url-edit.svg"
                                            alt=""
                                            className="cursor-pointer"
                                            onClick={() => setIsEditing(true)}
                                        />
                                    </Button>
                                </Box>
                            </Box>

                            {/* Edit state: text input + apply/reset icons */}
                            <Box style={{ display: isEditing ? 'block' : 'none' }}>
                                <Box display="flex" sx={{ width: '100%' }}>
                                    <Box sx={{ flexGrow: 1, paddingRight: '10px' }}>
                                        <TextField
                                            value={value || ''}
                                            variant="outlined"
                                            className="form-control-input form-control-wrapInput form-control-hg40"
                                            autoComplete="off"
                                            error={isError}
                                            helperText={errorMessage}
                                            fullWidth
                                            onChange={(e) => {
                                                onChange(e.target.value);
                                            }}
                                            onBlur={handleBlur}
                                        />
                                    </Box>
                                    <Box display="inline-flex">
                                        <Button
                                            className="button-url cursor-default"
                                        >
                                            <img
                                                src="/assets/image/button-url-apply.svg"
                                                alt=""
                                                className="cursor-pointer"
                                                onClick={() => handleApply(value)}
                                            />
                                        </Button>
                                        <Button
                                            className="button-url cursor-default"
                                            style={{ paddingRight: '0px' }}
                                        >
                                            <img
                                                src="/assets/image/button-url-reset.svg"
                                                alt=""
                                                className="cursor-pointer"
                                                onClick={() => handleReset(onChange)}
                                            />
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}
                />
            </div>
        </div>
    );
}

McpCatalogUrlField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    mode: PropTypes.oneOf([MCP_CATALOG_MODE.CREATE, MCP_CATALOG_MODE.EDIT]),
    defaultValue: PropTypes.string
};

McpCatalogUrlField.defaultProps = {
    required: false,
    mode: MCP_CATALOG_MODE.CREATE,
    defaultValue: ''
};

export default McpCatalogUrlField;
